/**
 * splitFirstMiddle — recover middle name from AI-returned FirstName.
 *
 * Per Microsoft's prebuilt-idDocument schema (v4.0 2024-11-30 GA): there is
 * NO dedicated MiddleName field for US driver's licenses. The FirstName
 * field is documented verbatim as "Given name and middle initial if
 * applicable" — so the model packs first + middle into one string.
 *
 * Empirical measurement across 66 state-DL samples (50 states + DC + extras):
 *   - MiddleName field populated: 0/66 (0%)
 *   - FirstName contains a space (middle packed): 24/31 clean samples (~77%)
 *
 * Per Bill's port decision (2026-04-23): apply this post-process before the
 * review dialog so the officer sees proposed first + middle split they can
 * accept/reject independently. Core Director Field 6 natively accepts
 * "First Name or First Name & Middle Initial Only", so either the raw or
 * the split can round-trip; the officer picks.
 *
 * Edge cases:
 *   - Compound first names ("Mary Kate", "Bobby Joe") will split wrong; the
 *     review dialog's per-field accept/reject handles this — officer
 *     rejects the middle proposal and the merged form stays in first name.
 *   - Commas from OCR ("MICHELLE, MARIE", "JUSTIN, WILLIAM" observed on NY
 *     and VA samples) are stripped before split.
 *   - Multiple middle names ("JOHN ROBERT EDWIN") collapse into middle with
 *     spaces preserved ("ROBERT EDWIN") — single middle slot in CD Field 936.
 */

export interface FirstMiddleSplit {
  first: string;
  middle: string;
}

/**
 * Split a raw AI-returned first-name string into first + middle components.
 *
 * @example
 *   splitFirstMiddle('MARK ALLEN')      // { first: 'MARK', middle: 'ALLEN' }
 *   splitFirstMiddle('JOSEPH A')        // { first: 'JOSEPH', middle: 'A' }
 *   splitFirstMiddle('MICHELLE, MARIE') // { first: 'MICHELLE', middle: 'MARIE' }
 *   splitFirstMiddle('JOHN')            // { first: 'JOHN', middle: '' }
 *   splitFirstMiddle('MARY JO ANN')     // { first: 'MARY', middle: 'JO ANN' }
 *   splitFirstMiddle('')                // { first: '', middle: '' }
 */
export function splitFirstMiddle(raw: string | undefined | null): FirstMiddleSplit {
  const cleaned = (raw ?? '').replace(/,\s*/g, ' ').trim();
  const parts = cleaned.split(/\s+/).filter((p) => p.length > 0);
  if (parts.length === 0) return { first: '', middle: '' };
  if (parts.length === 1) return { first: parts[0]!, middle: '' };
  return { first: parts[0]!, middle: parts.slice(1).join(' ') };
}
